/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import Axios from "axios";
import { FilterOperator } from "../constants/filterOperators";
import { IMfilesPropertyFilter, IValueListFilter } from "../interfaces/filters/IFilter";
import { 
  QUERYSTRING_CONFIG_PART, 
  QUERYSTRING_END_PART, 
  QUERYSTRING_PRESHARED_KEY, 
  QUERYSTRING_SEARCH_PART, 
  QUERYSTRING_START_PART, 
  QUERYSTRING_VIEW_PART, 
  QUERYSTRING_FILTER_PART, 
  QUERYSTRING_TOKEN_PART, 
  QUERYSTRING_REFRESH_PART,
  QUERYSTRING_SIDEBAR_WIDTH,
  QUERYSTRING_COLUMN_WIDTHS,
  QUERYSTRING_EXPAND_ALL,
  QUERYSTRING_SORT_BY,
} from "../constants/common";
import { IBaseConfiguration } from "../interfaces/configuration/IBaseConfiguration";
import { IExternalConfiguration } from "../interfaces/configuration/IExternalConfiguration";
import { MFConditionType } from "../constants/mfConditionTypes";

const GLOBAL_NAMESPACE_PREFIX = "Gantt.Global";
const USER_NAMESPACE_PREFIX = "Gantt.Users";
const GLOBAL_CONFIGURATION_NAMESPACE = `${GLOBAL_NAMESPACE_PREFIX}.Configuration`;
const USER_UISETTINGS_NAMESPACE = `${USER_NAMESPACE_PREFIX}.DisplaySettings`;
const USER_FILTERS_NAMESPACE = `${USER_NAMESPACE_PREFIX}.Filters`;
const BASECONFIG_JSON = "/baseConfig.json";

// Config
export const baseConfigUrl = () => process.env.PUBLIC_URL + BASECONFIG_JSON;

// NVS Api
export const fetchGlobalConfigurations = () => `/api/v1/${GLOBAL_CONFIGURATION_NAMESPACE}`;
export const fetchGlobalConfigurationUrl = (envName: string) => `/api/v1/${GLOBAL_CONFIGURATION_NAMESPACE}/${envName}`;
export const userDisplaySettingsUrl = (envName: string, userId: number) => `/api/v1/${USER_UISETTINGS_NAMESPACE}/${envName}-${userId}`;
export const userFiltersUrl = (envName: string, userId: number) => `/api/v1/${USER_FILTERS_NAMESPACE}/${envName}-${userId}`;
export const translationUrl = (lng: string) => `/api/v1/Translation/${lng}`;

export const remoteLogUrl = () => `/api/v1/log`;

// Vault operations
export const loginUrl = () => `/REST/server/authenticationtokens`;
export const loginTestUrl = () => `/REST/session/userid`;
export const authenticationProtocolsUrl = (vault: string) => `/REST/server/authenticationprotocols?vault=${vault}`;

// Session
export const sessionUrl = () => `/REST/session`;
export const sessionVaultUrl = () => `/REST/session/vault`;
export const userIdUrl = () => `/REST/session/userid`;

// Aliases
export const fetchObjectTypeIdByAliasUrl = () => `/REST/structure/objecttypes/itemidbyalias`;
export const fetchObjectClassIdByAliasUrl = () => `/REST/structure/classes/itemidbyalias`;
export const fetchPropertyIdByAliasUrl = () => `/REST/structure/properties/itemidbyalias`;
export const fetchWorkflowStateIdByAliasUrl = () => `/REST/structure/workflowstates/itemidbyalias`;
export const fetchWorkflowIdByAliasUrl = () => `/REST/structure/workflows/itemidbyalias`;

// Vault structure
export const fetchPropertyDefinitionsUrl = () => `/REST/structure/properties`;
export const fetchValueListsUrl = () => `/REST/valuelists`;
export const fetchValueListItemsUrl = (valueListId: number) => `/REST/valuelists/${valueListId}/items`;
export const fetchAllObjectClassesUrl = () => `/REST/structure/classes`;
export const fetchObjectTypeClassesUrl = (objectType: number) => `/REST/objects/${objectType}/classes`;
export const fetchAllObjectTypesUrl = () => `/REST/structure/objecttypes`;
export const fetchObjectTypeUrl = (objectTypeId: number) => `/REST/structure/objecttypes/${objectTypeId}`;
export const fetchAllWorkflowsUrl = () => `/REST/structure/workflows`;
export const fetchWorkflowStatesUrl = (workflowId: number) => `/REST/structure/workflows/${workflowId}/states`;

// Fetching items
export const fetchObjectsUrl = (objectType: number, filters: IMfilesPropertyFilter[], limit?: number) =>
  `/REST/objects/${objectType}${createQueryString(filters, limit)}`;
export const fetchSpecificObjectUrl = (objectType: number, objectId: number, objectVersion?: number) => `/REST/objects/${objectType}/${objectId}/${objectVersion ?? 'latest'}`;
export const fetchObjectVersionPropertiesUrl = (objectType: number, objectId: number, objectVersion: number) => `/REST/objects/${objectType}/${objectId}/${objectVersion}/properties`;
export const fetchObjectPropertiesUrl = (objectType: number, objectId: number) => `/REST/objects/${objectType}/${objectId}/properties`;
export const fetchPropertiesOfMultipleObjectsUrl = () => `/REST/objects/properties`;
export const fetchObjectsByQuickQueryUrl = (objectType: number, searchTerm: string, limit: number) =>
  `/REST/objects?o=${objectType}${searchTerm ? '&p0*=' + encodeURIComponent(searchTerm) + '' : ""}&limit=${limit}`;
export const fetchObjectsByExportedSearch = (searchTerm: string, limit:number) => `/REST/objects?exported=${searchTerm}&limit=${limit}`;


export const fetchObjectsByQuickQueryFilteredUrl = (objectType: number, searchTerm: string, propfilters: IMfilesPropertyFilter[], vlFilters: IValueListFilter[], limit: number) => {

  const condition = createQueryStringMFCondition(propfilters, vlFilters, limit);

  return `/REST/objects?o=${objectType}${searchTerm ? '&p0*=' + encodeURIComponent(searchTerm) + '' : ""}${condition ? '&' + condition : ''}`; 
}

// Views
export const fetchViewObjectsUrl = (path: string, limit: number) => `/REST/views/${path}/objects?limit=${limit}`;

// Checkedout objects
export const fetchCheckedoutObjectsUrl = () => `/REST/vault/extensionmethod/GetCheckedoutObjectVersionsOfType`;

// Creating items
export const createObjectOfTypeUrl = (objectTypeId: number) => `/REST/objects/${objectTypeId}`;

// Updating items
export const updateCheckedOutStatusUrl = (objectType: number, objectId: number) =>
  `/REST/objects/${objectType}/${objectId}/latest/checkedout`;
export const rollbackChangesUrl = (objectType: number, objectId: number) => `/REST/objects/${objectType}/${objectId}/latest`;
export const updateObjectStateUrl = (objectType: number, objectId: number) =>
  `/REST/objects/${objectType}/${objectId}/latest/workflowstate`;

// Helpers
export const filterOperatorToComparisonCharacter = new Map<FilterOperator, string>([
  [FilterOperator.Equal, "="],
  [FilterOperator.NotEqual, "!="],
  [FilterOperator.GreaterThan, ">>="],
  [FilterOperator.GreaterThanOrEqual, ">="],
  [FilterOperator.LessThan, "<<="],
  [FilterOperator.LessThanOrEqual, "<="],
  [FilterOperator.Like, "*="],
]);

export const createQueryString = (filters: IMfilesPropertyFilter[], limit?: number): string => {
  const filterParts =
    filters !== undefined && filters.length > 0 ? filters.map(getQueryStringPartFromFilter).filter((q) => q !== null) : [];
  const limitPart = limit ? `limit=${limit}` : "";

  return "?" + [...filterParts, limitPart].join("&");
};

const getQueryStringPartFromFilter = (filter: IMfilesPropertyFilter) => {
  const comparisonCharacter = filterOperatorToComparisonCharacter.get(filter.operator);
  return undefined === comparisonCharacter ? null : `p${filter.propertyId}${comparisonCharacter}${filter.value}`;
};

export const mfConditionTypeToComparisonCharacter = new Map<MFConditionType, string>([
  [MFConditionType.Equal, "="],
  [MFConditionType.NotEqual, "!="],
  [MFConditionType.GreaterThan, ">>="],
  [MFConditionType.GreaterThanOrEqual, ">="],
  [MFConditionType.LessThan, "<<="],
  [MFConditionType.LessThanOrEqual, "<="],
  [MFConditionType.Contains, "*="],
]);

export const createQueryStringMFCondition = (propFilters: IMfilesPropertyFilter[], vlFilters: IValueListFilter[], limit?: number): string => {
  const filterParts     = propFilters?.map(getQueryStringPartFromFilterMFCondition).filter((q) => q !== null) ?? [];
  const valueListParts = vlFilters?.map(getValueListQueryStringPartFromFilterMFCondition).filter((q) => q !== null) ?? [];
  const limitPart      = limit ? `limit=${limit}` : "";

  return [...filterParts, ...valueListParts, limitPart].join("&");
};

const getQueryStringPartFromFilterMFCondition = (filter: IMfilesPropertyFilter) => {
  const comparisonCharacter = mfConditionTypeToComparisonCharacter.get(filter.operator as number);
  return comparisonCharacter ? `p${filter.propertyId}${comparisonCharacter}${encodeURIComponent(filter.value)}` : null;
};

const getValueListQueryStringPartFromFilterMFCondition = (vlFilter: IValueListFilter) => {
  const comparisonCharacter = mfConditionTypeToComparisonCharacter.get(vlFilter.operator as number);
  return comparisonCharacter ? `vl${vlFilter.id}${comparisonCharacter}${encodeURIComponent(vlFilter.value)}` : null;
};

export const isSelectedItemsInQueryString = (relevantObjectTypes: number[]) => {
  const selectedItemsInfo = parseSelectedItemsFromQueryString(relevantObjectTypes);
  return selectedItemsInfo?.size > 0;
};

export const isSelectedViewInQueryString = () => {
  const params = new URLSearchParams(decodeURIComponent(window.location.search));
  const keys = Array.from(params.keys());
  return keys.length > 0 && keys.includes(QUERYSTRING_VIEW_PART) === true;
};

export const isSearchParamsInQueryString = () => {
  const params = new URLSearchParams(decodeURIComponent(window.location.search));
  const keys = Array.from(params.keys());
  return keys.length > 0 && keys.includes(QUERYSTRING_SEARCH_PART) === true;
};

export const parseSelectedItemsFromQueryString = (relevantObjectTypes: number[]): Map<number, number[]> => {
  const params = new URLSearchParams(decodeURIComponent(window.location.search));
  const result = new Map<number, number[]>();
  for (const [key, value] of params.entries()) {
    const objectType = parseInt(key);
    const objectId = parseInt(value);
    if (isNaN(objectType) || isNaN(objectId) || relevantObjectTypes.includes(objectType) === false) {
      continue;
    }

    if (result.get(objectType) == null) {
      result.set(objectType, [objectId]);
    } else {
      result.get(objectType).push(objectId);
    }
  }
  return result;
};

export const parseViewIdFromQueryString = (): string => {
  const params = new URLSearchParams(decodeURIComponent(window.location.search));
  return params.get(QUERYSTRING_VIEW_PART);
};

export const parseSearchParamsFromQueryString = (): string => {
  const params = new URLSearchParams(decodeURIComponent(window.location.search));
  return params.get(QUERYSTRING_SEARCH_PART);
};

export const parseStartDateFromQueryString = (): string => {
  const params = new URLSearchParams(decodeURIComponent(window.location.search));
  return params.get(QUERYSTRING_START_PART);
};

export const parseEndDateFromQueryString = (): string => {
  const params = new URLSearchParams(decodeURIComponent(window.location.search));
  return params.get(QUERYSTRING_END_PART);
};

export const parsePresharedKeyFromQueryString = ():string => {
  const params = new URLSearchParams(decodeURIComponent(window.location.search));
  return params.get(QUERYSTRING_PRESHARED_KEY);
};

export const parseTokenFromQueryString = ():string => {
  const params = new URLSearchParams(decodeURIComponent(window.location.search));
  return params.get(QUERYSTRING_TOKEN_PART);
};

export const parseFilterFromQueryString = ():string => {
  const params = new URLSearchParams(decodeURIComponent(window.location.search));
  return params.get(QUERYSTRING_FILTER_PART);
};

export const parseSideBarWidthFromQueryString = ():string => {
  const params = new URLSearchParams(decodeURIComponent(window.location.search));
  return params.get(QUERYSTRING_SIDEBAR_WIDTH);
};

export const parseColumnWidthsFromQueryString = (viewIndex: number):string => {
  const params = new URLSearchParams(decodeURIComponent(window.location.search));
  return params.get(QUERYSTRING_COLUMN_WIDTHS + viewIndex);
};

export const parseExpandAllFromQueryString = (viewIndex: number):string => {
  const params = new URLSearchParams(decodeURIComponent(window.location.search));
  return params.get(QUERYSTRING_EXPAND_ALL + viewIndex);
};

export const parseSortByFromQueryString = (viewIndex: number):string => {
  const params = new URLSearchParams(decodeURIComponent(window.location.search));
  return params.get(QUERYSTRING_SORT_BY + viewIndex);
};

export const parseRefreshFromQueryString = ():string => {
  const params = new URLSearchParams(decodeURIComponent(window.location.search));
  return params.get(QUERYSTRING_REFRESH_PART);
};

export const removeQueryParameter = (url: string, name: string) => {
  const urlParts = url.split(/[?#]/);
  const params = new URLSearchParams(urlParts.length > 1 ? decodeURIComponent(urlParts[1]) : undefined);
  if(params.has(name)) {
    params.delete(name);
  }
  const search = params.toString();
  const baseUrl = urlParts[0];
  return baseUrl + (search ? '?' + search : '');
};

export const addQueryParameter = (url: string, name: string, value: string) => {
  const urlParts = url.split(/[?#]/);
  const params = new URLSearchParams(urlParts.length > 1 ? decodeURIComponent(urlParts[1]) : undefined);
  if(value === null || value === undefined || value.length === 0) {
    if(params.has(name)) {
      params.delete(name);
    }
  } else { 
    params.set(name, value);
  }
  const search = params.toString();
  const baseUrl = urlParts[0];
  return baseUrl + (search ? '?' + search : '');
};

export const removeQueryParameters = (url: string) => {
  const paramsToKeep = [QUERYSTRING_CONFIG_PART];
  const params = new URLSearchParams(decodeURIComponent(url.split(/[?#]/)[1]));
  let ret = url.split(/[?#]/)[0];
  let firstParam = true;
  for(const paramName of paramsToKeep) {
    const paramValue = params.get(paramName);
    if(paramValue) {
      const delim = firstParam ? "?" : "&";
      ret = ret + delim + paramName + "=" + encodeURIComponent(paramValue);
      firstParam = false;
    }
  }
  return ret;
};

export const removeTrailingSlash = (str: string) => {
  return str.endsWith('/') ? str.slice(0, -1) : str;
}

export const getConfig = async (): Promise<IBaseConfiguration> => {
  const configResponse = await Axios.get(baseConfigUrl(), {headers: {'Cache-Control': 'no-cache'}});
  const config = (configResponse.data as unknown) as IBaseConfiguration;
  const params = new URLSearchParams(decodeURIComponent(window.location.search));
  const env = params.get(QUERYSTRING_CONFIG_PART);
  if(env) {
    config.envName = env;
  }
  if(config.nvsApiUrl) {
    config.nvsApiUrl = removeTrailingSlash(config.nvsApiUrl);
  }
  return config;
}

export const resolveMFilesApiBaseUrl = (baseConfiguration: IBaseConfiguration, externalConfig: IExternalConfiguration): string => {
  if(baseConfiguration.mfilesApiBaseUrl) {
    return baseConfiguration.mfilesApiBaseUrl;
  }
  return externalConfig.mfilesApiBaseUrl;
}

export const resolveVaultGuid = (baseConfiguration: IBaseConfiguration, externalConfig: IExternalConfiguration): string => {
  if(baseConfiguration.vaultGuid) {
    return baseConfiguration.vaultGuid;
  }
  return externalConfig.vaultGuid;
}